import { useSelector } from 'react-redux';
import {
  selectManagedResourcesAuth,
  selectManagedResourcesLoading,
  selectAuthIsForecaster,
  selectAuthIsAdmin
} from '../slices/authSlice';

/**
 * Hook to access managed resources and check permissions
 * @returns {Object} - Managed resources data and helper functions
 */
export const useManagedResources = () => {
  const managedResources = useSelector(selectManagedResourcesAuth);
  const loading = useSelector(selectManagedResourcesLoading);
  const isForecaster = useSelector(selectAuthIsForecaster);
  const isAdmin = useSelector(selectAuthIsAdmin);

  /**
   * Check if user manages a specific resource
   * @param {string} resourceId - The resource UUID
   * @returns {boolean}
   */
  const managesResource = (resourceId) => {
    if (isAdmin) return true; // Admins manage all resources
    if (isForecaster) return false; // Forecasters don't manage resources
    return managedResources.some(r => r.resourceId === resourceId);
  };

  /**
   * Get the role for a specific resource
   * @param {string} resourceId - The resource UUID
   * @returns {string|null} - Role name or null if not managing the resource
   */
  const getRoleForResource = (resourceId) => {
    if (isAdmin) return 'Admin';
    if (isForecaster) return null;
    const resource = managedResources.find(r => r.resourceId === resourceId);
    return resource ? resource.role : null;
  };

  /**
   * Get all managed resource IDs
   * @returns {Array<string>} - Array of resource UUIDs
   */
  const getManagedResourceIds = () => {
    if (isAdmin) return []; // Admins have access to all, no need to filter
    if (isForecaster) return [];
    return managedResources.map(r => r.resourceId);
  };

  /**
   * Check if user can edit a resource (Market Owner role)
   * @param {string} resourceId - The resource UUID
   * @returns {boolean}
   */
  const canEditResource = (resourceId) => {
    if (isAdmin) return true;
    if (isForecaster) return false;
    const resource = managedResources.find(r => r.resourceId === resourceId);
    return resource && resource.role === 'Market Owner';
  };

  /**
   * Check if user can view a resource (Market Owner or any other role)
   * @param {string} resourceId - The resource UUID
   * @returns {boolean}
   */
  const canViewResource = (resourceId) => {
    if (isAdmin) return true;
    if (isForecaster) return false; // Forecasters have different access patterns
    return managesResource(resourceId);
  };

  return {
    managedResources,
    loading,
    managesResource,
    getRoleForResource,
    getManagedResourceIds,
    canEditResource,
    canViewResource,
  };
};

export default useManagedResources;

